import { useCallback, useRef, useState } from 'react';
import { focusField } from '@munifocus/ui';

/**
 * One rule per field: the already-localized message when the field is not
 * acceptable, or `null` when it is. Computed on every render from the form's
 * current state, so a message clears the moment the officer fixes the field.
 */
export type FieldMessages<K extends string> = Record<K, string | null>;

/**
 * Anything focus can be moved to: an input element, or the imperative handle
 * MUI's `Select` hands its `inputRef` (which focuses the visible select).
 */
export interface Focusable {
  focus: () => void;
}

export interface SubmitValidation<K extends string> {
  /** Messages for the invalid fields — empty until the first submit attempt. */
  errors: Partial<Record<K, string>>;
  /**
   * Wraps the submit handler. The button stays enabled; pressing it with
   * something missing shows every field's message, moves focus to the first
   * invalid field (in rule order) and does not submit.
   */
  guard: (submit: () => void) => () => void;
  /** Spread onto a `TextField` (or pass the parts to any input). */
  field: (key: K) => {
    error: boolean;
    helperText: string | undefined;
    inputRef: (el: Focusable | null) => void;
  };
  /** For inputs that take a ref elsewhere — a Select's `inputRef`, a Checkbox's `slotProps.input.ref`. */
  register: (key: K) => (el: Focusable | null) => void;
  /** Forget the attempt — after a successful submit, or when a dialog closes. */
  reset: () => void;
  /**
   * How many fields are still invalid, once a submit has been attempted (0
   * before). For a short summary beside the button that was pressed: the
   * first field may be screens away from it.
   */
  invalidCount: number;
}

export function useSubmitValidation<K extends string>(
  messages: FieldMessages<K>,
): SubmitValidation<K> {
  const [attempted, setAttempted] = useState(false);
  const refs = useRef(new Map<K, Focusable>());

  const errors: Partial<Record<K, string>> = {};
  if (attempted) {
    for (const key of Object.keys(messages) as K[]) {
      const message = messages[key];
      if (message) errors[key] = message;
    }
  }

  const register = useCallback(
    (key: K) => (el: Focusable | null) => {
      if (el) refs.current.set(key, el);
      else refs.current.delete(key);
    },
    [],
  );

  // Rebuilt every render on purpose: the handler must judge the messages of
  // the render the officer pressed the button in.
  const guard = (submit: () => void) => () => {
    setAttempted(true);
    const firstInvalid = (Object.keys(messages) as K[]).find((k) => messages[k]);
    if (firstInvalid === undefined) {
      submit();
      return;
    }
    focusField(refs.current.get(firstInvalid));
  };

  const field = (key: K) => ({
    error: Boolean(errors[key]),
    helperText: errors[key],
    inputRef: register(key),
  });

  const reset = useCallback(() => setAttempted(false), []);

  const invalidCount = Object.keys(errors).length;

  return { errors, guard, field, register, reset, invalidCount };
}
